import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Button,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    FormText
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import { FaEdit, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import Footer from 'components/Footers/Footer';

const baseURL = apiURL1;

function ExamType() {
    const token = localStorage.getItem("token");

    const [examTypes, setExamTypes] = useState([]);
    const [modal, setModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [examTypeToDelete, setExamTypeToDelete] = useState(null);
    const [selectedExamType, setSelectedExamType] = useState(null);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchExamTypes();
    }, []);

    const fetchExamTypes = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseURL}/superadmin/examType`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                setExamTypes(response.data.data);
            } else {
                setExamTypes([]);
                if (response.data && !response.data.success) {
                    toast.error(response.data.message || 'Failed to fetch exam types');
                }
            }
        } catch (error) {
            console.error('Error fetching exam types:', error);
            toast.error('Error fetching exam types');
            setExamTypes([]);
        } finally {
            setLoading(false);
        }
    };

    const toggleModal = () => {
        setModal(!modal);
        setName('');
        setErrors({});
    };

    const toggleEditModal = (examTypeData) => {
        setEditModal(!editModal);
        setSelectedExamType(examTypeData);
        setName(examTypeData.name);
        setErrors({});
    };

    const openDeleteModal = (examTypeId) => {
        setExamTypeToDelete(examTypeId);
        setDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setExamTypeToDelete(null);
        setDeleteModal(false);
    };

    const validate = () => {
        let isValid = true;
        const newErrors = {};

        if (!name) {
            newErrors.name = 'Name is required';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleAddExamType = async () => {
        if (validate()) {
            setLoading(true);
            try {
                const newExamType = { name };
                const response = await axios.post(`${baseURL}/superadmin/examType`, newExamType, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Include the token in the headers
                    }
                });
                if (response.data.success) {
                    toast.success('Exam type added successfully!');
                    fetchExamTypes();
                    toggleModal();
                } else {
                    toast.error(response.data.message || 'Error adding exam type');
                }
            } catch (error) {
                console.error('Error adding exam type:', error);
                toast.error(error.response?.data?.message || 'Error adding exam type');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleUpdateExamType = async () => {
        if (validate()) {
            setLoading(true);
            try {
                const updatedExamType = { name };
                const response = await axios.put(`${baseURL}/superadmin/examType/${selectedExamType._id}`, updatedExamType, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Include the token in the headers
                    }
                });
                if (response.data.success) {
                    toast.success('Exam type updated successfully!');
                    fetchExamTypes();
                    toggleEditModal({});
                } else {
                    toast.error(response.data.message || 'Error updating exam type');
                }
            } catch (error) {
                console.error('Error updating exam type:', error);
                toast.error(error.response?.data?.message || 'Error updating exam type');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteExamType = async () => {
        setLoading(true);
        try {
            await axios.delete(`${baseURL}/superadmin/examType/${examTypeToDelete}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            toast.success('Exam type deleted successfully!');
            fetchExamTypes();
        } catch (error) {
            console.error('Error deleting exam type:', error);
            toast.error(error.response?.data?.message || 'Error deleting exam type');
        } finally {
            setLoading(false);
            closeDeleteModal();
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <Container>
                    <Row>
                        <Col>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Spinner color="primary" />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>Exam Type</h3>
                                <Button color="primary" onClick={toggleModal}>
                                    <FaPlus /> Add New
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <Table className="table">
                                    <thead>
                                        <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                            <th>Name</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {examTypes.map(examTypeData => (
                                            <tr key={examTypeData._id}>
                                                <td>{examTypeData.name}</td>
                                                <td>
                                                    <Button color="warning" size="sm" onClick={() => toggleEditModal(examTypeData)} style={{ marginRight: '5px' }}>
                                                        <FaEdit /> Edit
                                                    </Button>
                                                    <Button color="danger" size="sm" onClick={() => openDeleteModal(examTypeData._id)}>
                                                        <FaTrash /> Delete
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Add Exam Type Modal */}
                <Modal isOpen={modal} toggle={toggleModal}>
                    <ModalHeader toggle={toggleModal}>Add New Exam Type</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="name">Name</Label>
                            <Input
                                type="text"
                                name="name"
                                id="name"
                                placeholder="Enter name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                invalid={!!errors.name}
                            />
                            {errors.name && <FormText color="danger">{errors.name}</FormText>}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={handleAddExamType} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Add'}
                        </Button>
                        <Button color="secondary" onClick={toggleModal}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Edit Exam Type Modal */}
                <Modal isOpen={editModal} toggle={() => toggleEditModal({})}>
                    <ModalHeader toggle={() => toggleEditModal({})}>Edit Exam Type</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="name">Name</Label>
                            <Input
                                type="text"
                                name="name"
                                id="name"
                                placeholder="Enter name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                invalid={!!errors.name}
                            />
                            {errors.name && <FormText color="danger">{errors.name}</FormText>}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={handleUpdateExamType} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Update'}
                        </Button>
                        <Button color="secondary" onClick={() => toggleEditModal({})}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Delete Exam Type Modal */}
                <Modal isOpen={deleteModal} toggle={closeDeleteModal}>
                    <ModalHeader toggle={closeDeleteModal}>
                        Delete Exam Type
                        <Button
                            className="close"
                            onClick={closeDeleteModal}
                            style={{ position: 'absolute', top: '10px', right: '10px' }}
                        >
                            <FaTimes />
                        </Button>
                    </ModalHeader>
                    <ModalBody>
                        <p>Are you sure you want to delete this exam type?</p>
                        <Button color="danger" onClick={handleDeleteExamType}>
                            Yes, Delete
                        </Button>
                        <Button color="secondary" onClick={closeDeleteModal}>
                            Cancel
                        </Button>
                    </ModalBody>
                </Modal>
                <Footer />
            </Container>
        </>
    );
}

export default ExamType;
